/**
 * Block-id anchored edit plans (CONTRACTS §6 applyEditPlan).
 *
 * Ops:
 *   { op: 'replace_block'|'insert_after'|'insert_before'|'delete_block', blockId, html? }
 *   { op: 'replace_text', blockId?, find, replace }   exact, unique text match
 *   { op: 'fill_field', fieldId, html }                template or generated-document field
 *
 * Plans are applied to a ProseMirror transaction either directly (one undo step) or as a
 * review (diff marks / diff block attributes, resolved later by reviewEngine).
 */
import { createDocument } from '@tiptap/core';
import { Fragment, Mark } from '@tiptap/pm/model';

/** Default share of document text a plan may delete before it is refused as "gutted". */
export const GUTTED_DELETE_RATIO = 0.4;
/** Documents shorter than this (characters of text) are never treated as gutted. */
export const GUTTED_MIN_LENGTH = 200;

const FIELD_NODE_TYPES = new Set(['templateField', 'templateFieldBlock']);

/**
 * List top-level blocks that carry a block id.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @returns {Array<{id: string|null, pos: number, node: import('@tiptap/pm/model').Node, index: number}>} Blocks.
 */
export function getTopLevelBlocks(doc) {
  const blocks = [];
  doc.forEach((node, pos, index) => {
    blocks.push({ id: node.attrs.blockId || null, pos, node, index });
  });
  return blocks;
}

/**
 * Whether a node or any descendant is locked (`data-tpl-locked`).
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {boolean} True when locked content is present.
 */
export function containsLocked(node) {
  if (node.attrs && node.attrs.tplLocked) return true;
  let locked = false;
  node.descendants((child) => {
    if (locked) return false;
    if (child.attrs && child.attrs.tplLocked) locked = true;
    return !locked;
  });
  return locked;
}

/**
 * Collect template field ids and `{{variables}}` inside a fragment or node.
 * @param {import('@tiptap/pm/model').Node|Fragment} content - Content.
 * @returns {{fields: Set<string>, variables: Set<string>}} Tokens.
 */
function collectTemplateTokens(content) {
  const fields = new Set();
  const variables = new Set();
  const visit = (node) => {
    if (FIELD_NODE_TYPES.has(node.type.name) && node.attrs.id) fields.add(node.attrs.id);
    if (node.isText && node.text.indexOf('{{') !== -1) {
      (node.text.match(/\{\{\s*[a-zA-Z_][\w.]*\s*\}\}/g) || []).forEach((v) => variables.add(v.replace(/\s/g, '')));
    }
  };
  if (content instanceof Fragment) {
    content.descendants((node) => { visit(node); return true; });
  } else {
    visit(content);
    content.descendants((node) => { visit(node); return true; });
  }
  return { fields, variables };
}

/**
 * Parse HTML into block content for the schema. Block ids in the HTML are dropped: model
 * output often echoes the `data-block-id` of the block it was shown (getBlocks), and keeping
 * it would steal the id of an existing block. Ids are assigned explicitly by the caller.
 * @param {string} html - HTML.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {Fragment} Block fragment (empty when html has no content).
 */
export function parseBlocks(html, schema) {
  const source = String(html || '').trim();
  if (!source) return Fragment.empty;
  const doc = createDocument(source, schema);
  if (doc.childCount === 1 && doc.firstChild.isTextblock && doc.firstChild.content.size === 0) return Fragment.empty;
  const nodes = [];
  doc.content.forEach((node) => {
    nodes.push(node.attrs.blockId ? node.type.create({ ...node.attrs, blockId: null }, node.content, node.marks) : node);
  });
  return Fragment.fromArray(nodes);
}

/**
 * Put `blockId` on the first node of a fragment (when its type carries block ids).
 * @param {Fragment} fragment - Content.
 * @param {string} blockId - Id to keep.
 * @returns {Fragment} Content.
 */
function withFirstBlockId(fragment, blockId) {
  const first = fragment.firstChild;
  if (!first || !blockId || !Object.prototype.hasOwnProperty.call(first.attrs, 'blockId')) return fragment;
  return fragment.replaceChild(0, first.type.create({ ...first.attrs, blockId }, first.content, first.marks));
}

/**
 * Parse HTML into inline content (the content of the first textblock).
 * @param {string} html - HTML or plain text.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {Fragment} Inline fragment.
 */
function parseInline(html, schema) {
  const blocks = parseBlocks(html, schema);
  let inline = Fragment.empty;
  blocks.forEach((node) => {
    if (inline.size === 0) {
      if (node.isTextblock) inline = node.content;
      else node.descendants((child) => {
        if (inline.size === 0 && child.isTextblock) inline = child.content;
        return inline.size === 0;
      });
    }
  });
  return inline;
}

/**
 * Add a mark to every inline node of a fragment (recursively).
 * @param {Fragment} fragment - Content.
 * @param {import('@tiptap/pm/model').Mark} mark - Mark to add.
 * @returns {Fragment} Marked content.
 */
function markFragment(fragment, mark) {
  const nodes = [];
  fragment.forEach((node) => {
    if (node.isInline) {
      nodes.push(mark.type.isInSet(node.marks) ? node : node.mark(mark.addToSet(node.marks)));
    } else {
      nodes.push(node.copy(markFragment(node.content, mark)));
    }
  });
  return Fragment.fromArray(nodes);
}

/**
 * Tag a top-level node as inserted/deleted for review.
 * Falls back to marking its inline content when the type has no diff attributes.
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @param {'insert'|'delete'} kind - Review kind.
 * @param {string} changeId - Change id.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {import('@tiptap/pm/model').Node} Tagged node.
 */
function tagNode(node, kind, changeId, schema) {
  if (Object.prototype.hasOwnProperty.call(node.attrs, 'diffBlock')) {
    return node.type.create({ ...node.attrs, diffBlock: kind, diffChangeId: changeId }, node.content, node.marks);
  }
  const markType = schema.marks[kind === 'insert' ? 'diffInsert' : 'diffDelete'];
  return node.copy(markFragment(node.content, markType.create({ changeId })));
}

/**
 * Tag every node of a fragment for review.
 * @param {Fragment} fragment - Content.
 * @param {'insert'|'delete'} kind - Review kind.
 * @param {string} changeId - Change id.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {Fragment} Tagged content.
 */
function tagFragment(fragment, kind, changeId, schema) {
  const nodes = [];
  fragment.forEach((node) => nodes.push(tagNode(node, kind, changeId, schema)));
  return Fragment.fromArray(nodes);
}

/**
 * Mark the node at `from` as deleted for review, preferring an attribute change (keeps node
 * views alive) over replacing the node.
 * @param {import('@tiptap/pm/state').Transaction} tr - Transaction.
 * @param {number} from - Node position.
 * @param {import('@tiptap/pm/model').Node} node - Node at `from`.
 * @param {string} changeId - Change id.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {void}
 */
function markNodeDeleted(tr, from, node, changeId, schema) {
  if (Object.prototype.hasOwnProperty.call(node.attrs, 'diffBlock')) {
    tr.setNodeMarkup(from, undefined, { ...node.attrs, diffBlock: 'delete', diffChangeId: changeId }, node.marks);
  } else {
    tr.replaceWith(from, from + node.nodeSize, tagNode(node, 'delete', changeId, schema));
  }
}

/** Attributes that are review / addressing bookkeeping, not content. */
const BOOKKEEPING_ATTRS = new Set(['blockId', 'diffBlock', 'diffChangeId']);
const WORD_CHAR = /[\p{L}\p{N}_'’-]/u;

/**
 * Whether two nodes have the same type, content attributes and marks (ignoring ids/diff state).
 * @param {import('@tiptap/pm/model').Node} a - Node.
 * @param {import('@tiptap/pm/model').Node} b - Node.
 * @returns {boolean} Same shape.
 */
function sameShape(a, b) {
  if (a.type !== b.type || !Mark.sameSet(a.marks, b.marks)) return false;
  return Object.keys(a.attrs).every(
    (key) => BOOKKEEPING_ATTRS.has(key) || JSON.stringify(a.attrs[key]) === JSON.stringify(b.attrs[key])
  );
}

/**
 * Whether a node already carries review markup (diff marks or diff block attributes).
 * @param {import('@tiptap/pm/model').Node} node - Node.
 * @returns {boolean} Has pending review markup.
 */
function hasReviewMarkup(node) {
  let found = Boolean(node.attrs && node.attrs.diffBlock);
  node.descendants((child) => {
    if (found) return false;
    if ((child.attrs && child.attrs.diffBlock) || child.marks.some((m) => m.type.name === 'diffInsert' || m.type.name === 'diffDelete')) {
      found = true;
    }
    return !found;
  });
  return found;
}

/**
 * Collect the minimal inline edits that turn `oldNode` into `newNode` when both have the same
 * structure: one contiguous, word-snapped range per changed textblock. Returns false when the
 * structure differs (the caller then shows a whole-block replacement).
 * @param {import('@tiptap/pm/model').Node} oldNode - Current node.
 * @param {import('@tiptap/pm/model').Node} newNode - Replacement node.
 * @param {number} pos - Document position of `oldNode`.
 * @param {Array<{from: number, to: number, insert: Fragment}>} edits - Output list.
 * @returns {boolean} Whether an inline diff is possible.
 */
function collectInlineEdits(oldNode, newNode, pos, edits) {
  if (!sameShape(oldNode, newNode)) return false;
  if (oldNode.isTextblock) {
    const start = oldNode.content.findDiffStart(newNode.content);
    if (start == null) return true;
    let { a: endA, b: endB } = oldNode.content.findDiffEnd(newNode.content);
    const overlap = start - Math.min(endA, endB);
    if (overlap > 0) {
      endA += overlap;
      endB += overlap;
    }
    // Snap to whole words (the moved-over text is common to both sides, so this stays valid).
    const oldText = oldNode.textBetween(0, oldNode.content.size, '\n', '\uFFFC');
    let from = start;
    while (from > 0 && WORD_CHAR.test(oldText[from - 1])) from -= 1;
    const shift = start - from;
    let toA = endA;
    let toB = endB;
    while (toA < oldText.length && WORD_CHAR.test(oldText[toA])) {
      toA += 1;
      toB += 1;
    }
    edits.push({ from: pos + 1 + from, to: pos + 1 + toA, insert: newNode.content.cut(start - shift, toB) });
    return true;
  }
  if (oldNode.isLeaf || newNode.isLeaf) return oldNode.eq(newNode);
  if (oldNode.childCount !== newNode.childCount) return false;
  let ok = true;
  let offset = 0;
  for (let i = 0; i < oldNode.childCount && ok; i += 1) {
    const oldChild = oldNode.child(i);
    ok = collectInlineEdits(oldChild, newNode.child(i), pos + 1 + offset, edits);
    offset += oldChild.nodeSize;
  }
  return ok;
}

/**
 * Show a replace_block as word-level suggestions inside the existing block when the new html
 * keeps the block's structure (rewording, bolding, a changed sentence). Returns null when the
 * structure changed, so the caller falls back to deleted-block + inserted-block.
 * @param {import('@tiptap/pm/state').Transaction} tr - Transaction.
 * @param {{from: number, node: import('@tiptap/pm/model').Node}} block - Target block.
 * @param {Fragment} fragment - Replacement content.
 * @param {string} changeId - Change id.
 * @param {import('@tiptap/pm/model').Schema} schema - Schema.
 * @returns {'changed'|'unchanged'|null} Outcome.
 */
export function applyInlineReplace(tr, block, fragment, changeId, schema) {
  if (fragment.childCount !== 1 || hasReviewMarkup(block.node)) return null;
  const edits = [];
  if (!collectInlineEdits(block.node, fragment.firstChild, block.from, edits)) return null;
  if (edits.length === 0) return 'unchanged';
  const deleteMark = schema.marks.diffDelete.create({ changeId });
  const insertMark = schema.marks.diffInsert.create({ changeId });
  // Right to left: addMark keeps positions, and each insert lands after positions still to come.
  edits.sort((x, y) => y.from - x.from).forEach((edit) => {
    if (edit.insert.size) tr.insert(edit.to, markFragment(edit.insert, insertMark));
    if (edit.to > edit.from) tr.addMark(edit.from, edit.to, deleteMark);
  });
  return 'changed';
}

/**
 * Find a field target (template field node, generated field block or generated field mark range).
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {string} fieldId - Field id.
 * @returns {{type: 'templateField'|'templateFieldBlock'|'generatedField'|'generatedFieldMark', from: number, to: number, node?: object, mark?: object}|null} Target.
 */
function findField(doc, fieldId) {
  let target = null;
  doc.descendants((node, pos) => {
    if (target) return false;
    if (FIELD_NODE_TYPES.has(node.type.name) && node.attrs.id === fieldId) {
      target = { type: node.type.name, from: pos, to: pos + node.nodeSize, node };
      return false;
    }
    if (node.type.name === 'generatedField' && node.attrs.fieldId === fieldId) {
      target = { type: 'generatedField', from: pos, to: pos + node.nodeSize, node };
      return false;
    }
    if (node.isInline) {
      const mark = node.marks.find((m) => m.type.name === 'generatedFieldMark' && m.attrs.fieldId === fieldId);
      if (mark) {
        target = { type: 'generatedFieldMark', from: pos, to: pos + node.nodeSize, mark };
        // Extend over adjacent inline nodes that carry the same field mark.
        const $pos = doc.resolve(pos);
        const parent = $pos.parent;
        const start = $pos.start();
        let offset = pos - start + node.nodeSize;
        for (let i = $pos.index() + 1; i < parent.childCount; i += 1) {
          const sibling = parent.child(i);
          if (!sibling.marks.some((m) => m.type.name === 'generatedFieldMark' && m.attrs.fieldId === fieldId)) break;
          offset += sibling.nodeSize;
        }
        target.to = start + offset;
        return false;
      }
    }
    return true;
  });
  return target;
}

/** Character used for inline atoms (field chips, hard breaks) in matched text, as in find & replace. */
export const INLINE_ATOM_CHAR = '\uFFFC';

/**
 * Whether an inline node is shown as deleted in a review (it is gone once the review resolves
 * the way it is displayed).
 * @param {import('@tiptap/pm/model').Node} node - Inline node.
 * @returns {boolean} True when the node carries a diffDelete mark.
 */
const isReviewDeleted = (node) => node.marks.some((m) => m.type.name === 'diffDelete');

/**
 * Find exact, case-sensitive, non-overlapping matches of `term` in the document as it reads
 * with every pending suggestion applied: text marked for deletion and blocks marked as deleted
 * are skipped, suggested insertions count as text. Later ops of a plan therefore see the result
 * of earlier ops, exactly like direct mode and the server's validator. Matching runs per
 * textblock (a match never crosses a paragraph, list item or cell); inline atoms count as
 * U+FFFC. Without review markup this is the same as find & replace.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {string} term - Text to find.
 * @returns {Array<{from: number, to: number}>} Match ranges (may span hidden deleted text inside).
 */
export function findPlanTextMatches(doc, term) {
  const results = [];
  if (!term) return results;
  doc.descendants((node, pos) => {
    if (node.attrs && node.attrs.diffBlock === 'delete') return false;
    if (!node.isTextblock) return true;
    let text = '';
    const positions = [];
    let offset = pos + 1;
    node.forEach((child) => {
      if (!isReviewDeleted(child)) {
        const chunk = child.isText ? child.text : INLINE_ATOM_CHAR.repeat(child.nodeSize);
        for (let i = 0; i < chunk.length; i += 1) positions.push(offset + i);
        text += chunk;
      }
      offset += child.nodeSize;
    });
    let index = text.indexOf(term);
    while (index !== -1) {
      results.push({ from: positions[index], to: positions[index + term.length - 1] + 1 });
      index = text.indexOf(term, index + term.length);
    }
    return false;
  });
  return results;
}

/**
 * Describe how a replace_text range overlaps suggestions already in the review document.
 * @param {import('@tiptap/pm/model').Node} doc - Review document.
 * @param {number} from - Match start.
 * @param {number} to - Match end.
 * @returns {{changeIds: Set<string>, insertedBlock: boolean, segments: Array<{from: number, to: number, kind: 'live'|'inserted'|'deleted'}>}} Overlap.
 */
function describeReviewOverlap(doc, from, to) {
  const changeIds = new Set();
  let insertedBlock = false;
  const $from = doc.resolve(from);
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const ancestor = $from.node(depth);
    if (ancestor.attrs.diffBlock === 'insert' && ancestor.attrs.diffChangeId) {
      insertedBlock = true;
      changeIds.add(String(ancestor.attrs.diffChangeId));
      break;
    }
  }
  const segments = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isInline) return true;
    const segment = { from: Math.max(pos, from), to: Math.min(pos + node.nodeSize, to), kind: 'live' };
    const deleted = node.marks.filter((m) => m.type.name === 'diffDelete');
    const inserted = node.marks.filter((m) => m.type.name === 'diffInsert');
    if (deleted.length) {
      segment.kind = 'deleted';
      deleted.forEach((m) => { if (m.attrs.changeId) changeIds.add(String(m.attrs.changeId)); });
    } else if (inserted.length) {
      segment.kind = 'inserted';
      inserted.forEach((m) => { if (m.attrs.changeId) changeIds.add(String(m.attrs.changeId)); });
    }
    segments.push(segment);
    return false;
  });
  return { changeIds, insertedBlock, segments };
}

/**
 * Top-level block containing a position.
 * @param {import('@tiptap/pm/model').Node} doc - Document.
 * @param {number} pos - Position.
 * @returns {{pos: number, node: import('@tiptap/pm/model').Node}|null} Block.
 */
function topLevelAt(doc, pos) {
  const $pos = doc.resolve(Math.max(0, Math.min(pos, doc.content.size)));
  if ($pos.depth === 0) {
    const node = doc.nodeAt(pos);
    return node ? { pos, node } : null;
  }
  const start = $pos.before(1);
  return { pos: start, node: doc.nodeAt(start) };
}

/**
 * Apply an edit plan to a transaction.
 * @param {import('@tiptap/pm/state').EditorState} state - Base state (must be the state `tr` was created from).
 * @param {Array<object>} ops - Plan operations.
 * @param {{mode?: 'direct'|'review', changeIdPrefix?: string, allowFieldRemoval?: boolean}} [options] - Options.
 * @returns {{tr: import('@tiptap/pm/state').Transaction, applied: Array<{op: object, changeId?: string}>, failed: Array<{op: object, reason: string}>}} Result.
 */
export function applyPlanToTransaction(state, ops, options = {}) {
  const { schema } = state;
  const mode = options.mode === 'review' ? 'review' : 'direct';
  const prefix = options.changeIdPrefix || 'c';
  const tr = state.tr;
  const applied = [];
  const failed = [];
  const blockIndex = new Map();
  getTopLevelBlocks(state.doc).forEach((block) => {
    if (block.id && !blockIndex.has(block.id)) blockIndex.set(block.id, block);
  });
  let changeCounter = 0;
  const nextChangeId = () => {
    changeCounter += 1;
    return `${prefix}${changeCounter}`;
  };

  /**
   * Resolve a block id to its current (mapped) position and node.
   * @param {string} blockId - Block id.
   * @returns {{from: number, to: number, node: object}|null} Current range.
   */
  const resolveBlock = (blockId) => {
    const block = blockIndex.get(blockId);
    if (!block) return null;
    const mapped = tr.mapping.mapResult(block.pos, 1);
    const node = mapped.deleted ? null : tr.doc.nodeAt(mapped.pos);
    if (node && node.attrs.blockId === blockId && node.attrs.diffBlock !== 'delete') {
      return { from: mapped.pos, to: mapped.pos + node.nodeSize, node };
    }
    // The block was replaced by an earlier op that kept its id: find it in the current doc,
    // preferring the live replacement over a copy marked as deleted for review.
    let found = null;
    let deletedCopy = null;
    tr.doc.forEach((child, pos) => {
      if (found || child.attrs.blockId !== blockId) return;
      const range = { from: pos, to: pos + child.nodeSize, node: child };
      if (child.attrs.diffBlock === 'delete') deletedCopy = deletedCopy || range;
      else found = range;
    });
    return found || deletedCopy;
  };

  /**
   * Refuse an op when it would drop template fields or variables from `oldContent`.
   * @param {object} oldContent - Node being replaced.
   * @param {Fragment} newContent - Replacement.
   * @returns {string|null} Failure reason.
   */
  const templateTokenLoss = (oldContent, newContent) => {
    if (options.allowFieldRemoval) return null;
    const before = collectTemplateTokens(oldContent);
    const after = collectTemplateTokens(newContent);
    const lostFields = [...before.fields].filter((id) => !after.fields.has(id));
    const lostVars = [...before.variables].filter((v) => !after.variables.has(v));
    if (lostFields.length) return `would_remove_template_field: ${lostFields.join(', ')}`;
    if (lostVars.length) return `would_remove_variable: ${lostVars.join(', ')}`;
    return null;
  };

  (Array.isArray(ops) ? ops : []).forEach((op) => {
    if (!op || typeof op !== 'object') {
      failed.push({ op, reason: 'invalid_op' });
      return;
    }
    const kind = String(op.op || op.type || '');
    try {
      if (kind === 'replace_block' || kind === 'delete_block' || kind === 'insert_after' || kind === 'insert_before') {
        if (!op.blockId) {
          failed.push({ op, reason: 'missing_block_id' });
          return;
        }
        const block = resolveBlock(op.blockId);
        if (!block) {
          const valid = [...blockIndex.keys()];
          failed.push({ op, reason: `unknown_block_id: ${op.blockId}. Valid ids: ${valid.slice(0, 60).join(', ')}${valid.length > 60 ? ', …' : ''}` });
          return;
        }
        const isInsert = kind === 'insert_after' || kind === 'insert_before';
        if (!isInsert && containsLocked(block.node)) {
          failed.push({ op, reason: `locked_block: ${op.blockId} is locked by the template author` });
          return;
        }
        const fragment = kind === 'delete_block' ? Fragment.empty : parseBlocks(op.html, schema);
        if (kind !== 'delete_block' && fragment.size === 0) {
          failed.push({ op, reason: 'empty_html' });
          return;
        }
        if (!isInsert) {
          const loss = templateTokenLoss(block.node, fragment);
          if (loss) {
            failed.push({ op, reason: loss });
            return;
          }
        }
        const changeId = nextChangeId();
        if (mode === 'direct') {
          if (kind === 'insert_after') tr.insert(block.to, fragment);
          else if (kind === 'insert_before') tr.insert(block.from, fragment);
          else if (kind === 'delete_block') {
            if (tr.doc.childCount === 1) tr.replaceWith(block.from, block.to, schema.nodes.paragraph.create());
            else tr.delete(block.from, block.to);
          } else {
            // Keep the block id on the first replacement block so the block stays addressable.
            tr.replaceWith(block.from, block.to, withFirstBlockId(fragment, op.blockId));
          }
        } else if (kind === 'insert_after') {
          tr.insert(block.to, tagFragment(fragment, 'insert', changeId, schema));
        } else if (kind === 'insert_before') {
          tr.insert(block.from, tagFragment(fragment, 'insert', changeId, schema));
        } else {
          if (kind === 'replace_block') {
            const inline = applyInlineReplace(tr, block, fragment, changeId, schema);
            if (inline === 'unchanged') {
              failed.push({ op, reason: `no_change: ${op.blockId} already reads like that` });
              return;
            }
            if (inline === 'changed') {
              applied.push({ op, changeId });
              return;
            }
          }
          const stepsBefore = tr.steps.length;
          markNodeDeleted(tr, block.from, block.node, changeId, schema);
          if (kind === 'replace_block') {
            // The replacement shares the original's id while the review is open (BlockId does
            // not re-stamp during review): accepting keeps the id on the new block, rejecting
            // on the original one.
            const mappedEnd = tr.mapping.slice(stepsBefore).map(block.to, 1);
            tr.insert(mappedEnd, tagFragment(withFirstBlockId(fragment, op.blockId), 'insert', changeId, schema));
          }
        }
        applied.push({ op, changeId });
        return;
      }

      if (kind === 'replace_text') {
        const find = typeof op.find === 'string' ? op.find : '';
        const replace = typeof op.replace === 'string' ? op.replace : '';
        if (!find) {
          failed.push({ op, reason: 'missing_find' });
          return;
        }
        let scopeFrom = 0;
        let scopeTo = tr.doc.content.size;
        if (op.blockId) {
          const block = resolveBlock(op.blockId);
          if (!block) {
            failed.push({ op, reason: `unknown_block_id: ${op.blockId}` });
            return;
          }
          scopeFrom = block.from;
          scopeTo = block.to;
        }
        const matches = findPlanTextMatches(tr.doc, find).filter((m) => m.from >= scopeFrom && m.to <= scopeTo);
        if (matches.length === 0) {
          failed.push({ op, reason: `text_not_found: "${find.slice(0, 80)}"${op.blockId ? ` in ${op.blockId}` : ''}` });
          return;
        }
        if (matches.length > 1) {
          failed.push({ op, reason: `ambiguous_match: "${find.slice(0, 80)}" appears ${matches.length} times; quote more context or pass a blockId` });
          return;
        }
        const { from, to } = matches[0];
        const owner = topLevelAt(tr.doc, from);
        if (owner && containsLocked(owner.node)) {
          failed.push({ op, reason: 'locked_block: text is inside a locked block' });
          return;
        }
        let lockedInline = false;
        tr.doc.nodesBetween(from, to, (node) => {
          if (FIELD_NODE_TYPES.has(node.type.name)) lockedInline = true;
          return !lockedInline;
        });
        if (lockedInline && !options.allowFieldRemoval) {
          failed.push({ op, reason: 'would_remove_template_field: match spans a template field' });
          return;
        }
        const marks = (tr.doc.nodeAt(from) || { marks: [] }).marks
          .filter((m) => m.type.name !== 'diffDelete' && m.type.name !== 'diffInsert');
        if (mode === 'direct') {
          if (replace) tr.replaceWith(from, to, schema.text(replace, marks));
          else tr.delete(from, to);
          applied.push({ op, changeId: nextChangeId() });
          return;
        }
        // Review: an edit of text that an earlier op of this plan suggested joins that
        // suggestion, so accepting or rejecting it resolves both edits together and never keeps
        // half of each.
        const overlap = describeReviewOverlap(tr.doc, from, to);
        if (overlap.changeIds.size > 1) {
          failed.push({ op, reason: `overlaps_pending_change: "${find.slice(0, 80)}" spans ${overlap.changeIds.size} earlier changes of this plan; merge them into one op` });
          return;
        }
        const changeId = overlap.changeIds.size === 1 ? [...overlap.changeIds][0] : nextChangeId();
        if (overlap.insertedBlock) {
          // The whole block is a pending insertion: edit its text in place.
          if (replace) tr.replaceWith(from, to, schema.text(replace, marks));
          else tr.delete(from, to);
          applied.push({ op, changeId });
          return;
        }
        const stepsBefore = tr.steps.length;
        const deleteMark = schema.marks.diffDelete.create({ changeId });
        // Right to left so earlier segment positions stay valid. Suggested (not yet accepted)
        // text is withdrawn outright, live text is marked for deletion, hidden text stays as is.
        [...overlap.segments].reverse().forEach((segment) => {
          if (segment.kind === 'inserted') tr.delete(segment.from, segment.to);
          else if (segment.kind === 'live') tr.addMark(segment.from, segment.to, deleteMark);
        });
        if (replace) {
          const insertAt = tr.mapping.slice(stepsBefore).map(to, -1);
          tr.insert(insertAt, schema.text(replace, [...marks, schema.marks.diffInsert.create({ changeId })]));
        }
        applied.push({ op, changeId });
        return;
      }

      if (kind === 'fill_field') {
        if (!op.fieldId) {
          failed.push({ op, reason: 'missing_field_id' });
          return;
        }
        const target = findField(tr.doc, op.fieldId);
        if (!target) {
          failed.push({ op, reason: `unknown_field_id: ${op.fieldId}` });
          return;
        }
        const owner = topLevelAt(tr.doc, target.from);
        if (owner && owner.node.attrs.tplLocked) {
          failed.push({ op, reason: 'locked_block: field is inside a locked block' });
          return;
        }
        const changeId = nextChangeId();
        const isInlineTarget = target.type === 'templateField' || target.type === 'generatedFieldMark';
        if (isInlineTarget) {
          let inline = parseInline(op.html, schema);
          if (inline.size === 0) {
            failed.push({ op, reason: 'empty_html' });
            return;
          }
          if (target.type === 'generatedFieldMark') {
            const fieldMark = schema.marks.generatedFieldMark.create({ ...target.mark.attrs, status: 'filled' });
            inline = markFragment(inline, fieldMark);
          }
          if (mode === 'direct') {
            tr.replaceWith(target.from, target.to, inline);
          } else {
            tr.addMark(target.from, target.to, schema.marks.diffDelete.create({ changeId }));
            tr.insert(target.to, markFragment(inline, schema.marks.diffInsert.create({ changeId })));
          }
        } else {
          let blocks = parseBlocks(op.html, schema);
          if (blocks.size === 0) {
            failed.push({ op, reason: 'empty_html' });
            return;
          }
          if (target.type === 'generatedField') {
            blocks = Fragment.from(schema.nodes.generatedField.create({ ...target.node.attrs, status: 'filled' }, blocks));
          }
          if (mode === 'direct') {
            tr.replaceWith(target.from, target.to, blocks);
          } else {
            const stepsBefore = tr.steps.length;
            markNodeDeleted(tr, target.from, target.node, changeId, schema);
            tr.insert(tr.mapping.slice(stepsBefore).map(target.to, 1), tagFragment(blocks, 'insert', changeId, schema));
          }
        }
        applied.push({ op, changeId });
        return;
      }

      failed.push({ op, reason: `unsupported_op: ${kind || 'missing op'}` });
    } catch (error) {
      failed.push({ op, reason: `apply_error: ${error?.message || String(error)}` });
    }
  });

  return { tr, applied, failed };
}

/**
 * Check whether a direct application would gut the document.
 * @param {import('@tiptap/pm/model').Node} before - Document before.
 * @param {import('@tiptap/pm/model').Node} after - Document after.
 * @param {number} [ratio] - Max share of text that may be deleted.
 * @returns {string|null} Refusal reason or null.
 */
export function guttedEditReason(before, after, ratio = GUTTED_DELETE_RATIO) {
  const oldLength = before.textContent.length;
  const newLength = after.textContent.length;
  if (oldLength < GUTTED_MIN_LENGTH) return null;
  const removed = (oldLength - newLength) / oldLength;
  if (removed > ratio) {
    return `gutted_edit: this would delete ${Math.round(removed * 100)}% of the document text; ask the user to confirm a large deletion first`;
  }
  return null;
}
